import {
  Battery90,
  Battery80,
  Battery60,
  Battery50,
  Battery30,
  Battery20,
  BatteryAlert,
  BatteryChargingFull,
  Wifi,
  WifiOff,
} from "@mui/icons-material";

export function isDataStale(lastUpdated) {
  if (!lastUpdated) return true;

  const updateTime = new Date(lastUpdated);
  const now = new Date();
  const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);

  return updateTime < threeDaysAgo;
}

export function formatLastUpdated(lastUpdated) {
  if (!lastUpdated) return "unknown time";

  try {
    const date = new Date(lastUpdated);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  } catch {
    return "unknown time";
  }
}

// device_logs.response_codes is a JSON array of { code, description }, and may
// arrive either already parsed or as a JSON string depending on the driver.
export function parseResponseCodes(rawResponseCodes) {
  if (!rawResponseCodes) return [];

  let parsed = rawResponseCodes;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((entry) => {
      if (typeof entry === "string") {
        return { code: entry, description: "" };
      }
      if (entry && typeof entry === "object") {
        return {
          code: entry.code ? String(entry.code) : "",
          description: entry.description ? String(entry.description) : "",
        };
      }
      return null;
    })
    .filter((entry) => entry && (entry.code || entry.description));
}

export function formatResponseCode({ code, description }) {
  if (code && description) return `${code}: ${description}`;
  return code || description;
}

export function getRobotStatus(device) {
  if (isDataStale(device.log_timestamp)) {
    return {
      color: "#9e9e9e",
      status: "Stale Data",
      isStale: true,
      lastUpdated: device.log_timestamp || null,
      severity: 3,
    };
  }

  const isOnline = device.connected === 1 || device.connected === true;
  const batteryLevel = device.battery_level || 0;
  const operatingState = device.operating_state;
  const movementState = device.movement_state;

  if (!isOnline) {
    return {
      color: "#f44336",
      status: "Offline",
      isStale: false,
      severity: 0,
    };
  }

  if (operatingState === "error" || movementState === "error") {
    return {
      color: "#f44336",
      status: "Error",
      isStale: false,
      severity: 0,
      responseCodes: parseResponseCodes(device.response_codes),
    };
  }

  if (batteryLevel < 20) {
    return {
      color: "#ff9800",
      status: "Low Battery",
      isStale: false,
      severity: 1,
    };
  }

  if (operatingState === "paused" || movementState === "paused") {
    return {
      color: "#ff9800",
      status: "Paused",
      isStale: false,
      severity: 1,
    };
  }

  return {
    color: "#4caf50",
    status: "Normal",
    isStale: false,
    severity: 2,
  };
}

export function getRobotStatusCategory(device) {
  const robotStatus = getRobotStatus(device);

  // Alert includes critical states and stale data.
  if (robotStatus.severity === 0 || robotStatus.severity === 3) {
    return "alert";
  }

  if (robotStatus.severity === 1) {
    return "warning";
  }

  return "good";
}

export function getBatteryIcon(batteryLevel, device) {
  const isCharging =
    device.operating_state === "docked" ||
    device.movement_state === "charging" ||
    device.is_charging === 1 ||
    device.operating_state === "charging";

  if (isCharging) return BatteryChargingFull;
  if (batteryLevel >= 90) return Battery90;
  if (batteryLevel >= 80) return Battery80;
  if (batteryLevel >= 60) return Battery60;
  if (batteryLevel >= 50) return Battery50;
  if (batteryLevel >= 30) return Battery30;
  if (batteryLevel >= 20) return Battery20;
  return BatteryAlert;
}

export function getBatteryColor(batteryLevel, isOnline, isStale) {
  if (isStale) return "#9e9e9e";
  if (!isOnline) return "#9e9e9e";
  if (batteryLevel >= 50) return "#4caf50";
  if (batteryLevel >= 20) return "#ff9800";
  return "#f44336";
}

export function getConnectionIcon(isOnline) {
  return isOnline ? Wifi : WifiOff;
}

export function getConnectionColor(isOnline, isStale) {
  if (isStale) return "#9e9e9e";
  return isOnline ? "#4caf50" : "#9e9e9e";
}

export function getConnectionTooltip(isOnline, isStale) {
  if (isStale) return "Connection status unknown (stale data)";
  return isOnline ? "Connected" : "Disconnected";
}

export function compareDevicesForDashboard(leftDevice, rightDevice) {
  const leftStatusCategory = getRobotStatusCategory(leftDevice);
  const rightStatusCategory = getRobotStatusCategory(rightDevice);

  const statusPriority = {
    alert: 0,
    warning: 1,
    good: 2,
  };

  const leftCategoryPriority = statusPriority[leftStatusCategory] ?? 99;
  const rightCategoryPriority = statusPriority[rightStatusCategory] ?? 99;

  if (leftCategoryPriority !== rightCategoryPriority) {
    return leftCategoryPriority - rightCategoryPriority;
  }

  const leftStatus = getRobotStatus(leftDevice);
  const rightStatus = getRobotStatus(rightDevice);

  // Within alert group, show true critical errors/offline before stale data.
  const leftAlertDetail = leftStatus.severity === 0 ? 0 : 1;
  const rightAlertDetail = rightStatus.severity === 0 ? 0 : 1;

  if (
    leftStatusCategory === "alert" &&
    rightStatusCategory === "alert" &&
    leftAlertDetail !== rightAlertDetail
  ) {
    return leftAlertDetail - rightAlertDetail;
  }

  if (leftStatus.severity !== rightStatus.severity) {
    return leftStatus.severity - rightStatus.severity;
  }

  const leftName = (leftDevice.name || "").toLowerCase();
  const rightName = (rightDevice.name || "").toLowerCase();

  return leftName.localeCompare(rightName);
}
